import React, { useState } from "react";
import { Button, Col, Form, Input, Modal, Row, Select, Switch, message } from "antd";

import { templatesApi } from "../../../api/presentationsApi";
import {
  IPresentationTemplate,
  NAMESPACE_LABELS,
  TemplateScope,
} from "../../../types/models/presentations";
import TemplateScopeFields, { scopeOf } from "./TemplateScopeFields";
import DeclareCopyFieldsModal from "../copy/DeclareCopyFieldsModal";

/**
 * Everything about a template except the file itself.
 *
 * Re-targeting is the edit that actually gets made: a template goes up as the
 * default, then a second one arrives for Vail and the first has to stop covering
 * it. That was previously only possible in the Django admin.
 *
 * So is changing what the template's copy lists are for: those descriptions
 * are only asked for once, at upload, and this is the one place to revise them.
 *
 * Changing the file is deliberately not here — a new .pptx invalidates the
 * manifest, the preview and the thumbnail, so it goes through upload, where it
 * gets checked first.
 */

function TemplateEditModal({
  template,
  onClose,
  onSaved,
}: {
  template: IPresentationTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const initialScope = scopeOf(template.destination, template.listings);
  const [scope, setScope] = useState<TemplateScope>(initialScope);
  // Lists still undescribed are offered from the card instead — editing needs
  // a description to start from.
  const describedCopy = (template.copy_field_keys ?? []).filter(
    (key) => !(template.undeclared_copy_keys ?? []).includes(key));

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { scope: chosen, destination, listings, ...rest } = values;
      // Both targeting fields are always sent. Re-targeting means clearing one
      // as well as setting the other, and the API rejects a template that
      // carries both.
      await templatesApi.update(Number(template.id), {
        ...rest,
        destination: chosen === "destination" ? destination : null,
        listings: chosen === "listing" ? listings : [],
      });
      message.success("Template updated.");
      onSaved();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data;
      message.error(
        detail?.destination ?? detail?.detail ?? "Could not save those changes."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      title="Edit template"
      onCancel={onClose}
      onOk={save}
      okText="Save"
      okButtonProps={{ loading: saving }}
      width={640}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: template.name,
          namespace: template.namespace,
          language: template.language,
          is_active: template.is_active,
          publishes_to_drive: template.publishes_to_drive,
          scope: initialScope,
          destination: template.destination ?? undefined,
          listings: template.listings ?? [],
        }}
        onValuesChange={(changed) => {
          if (changed.scope) setScope(changed.scope);
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Give it a name" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="namespace" label="Type">
              <Select
                options={Object.entries(NAMESPACE_LABELS).map(
                  ([value, label]) => ({ value, label })
                )}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="language" label="Language">
              <Select
                options={[
                  { value: "es", label: "Spanish" },
                  { value: "en", label: "English" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <TemplateScopeFields scope={scope} />

        <Form.Item
          name="is_active"
          label="Active"
          valuePropName="checked"
          extra="An inactive template is never chosen for a property. Anything already made from it is kept."
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="publishes_to_drive"
          label="Send PDFs to Drive"
          valuePropName="checked"
          extra={"Every render goes to the property's folder in \u201cSEND THESE \u2014 "
            + "Current Brochures\u201d, once someone has confirmed that folder. "
            + "Leave off for templates still being tried out."}
        >
          <Switch />
        </Form.Item>

        {describedCopy.length > 0 && (
          <Form.Item
            label="Copy descriptions"
            extra={`What each of this template's ${describedCopy.length} copy `
              + "list(s) is for, and whether AI may draft it. Saved on their own, "
              + "straight away."}
          >
            <DeclareCopyFieldsModal
              mode="edit"
              keys={describedCopy}
              templateId={template.id}
              shapes={template.copy_lists}
              slidesByKey={template.manifest?.token_slides}
              onDeclared={onSaved}
            >
              <Button>Edit copy descriptions</Button>
            </DeclareCopyFieldsModal>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

export default TemplateEditModal;
